import { BoardDetailData, BoardViewType } from '@/types/board';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, SxProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { scrollToElement } from '@/utils';
import {
  changeBoardBookmark,
  changeLikeBoard,
  useDeleteBoard,
} from '@/hooks/apiHooks';
import BoardDetailHeader from '@/components/Board/BoardDetail/Content/BoardDetailHeader';
import { useIsAppEmbedded } from '@/utils/appEmbedded';
import BoardDetailContent from '@/components/Board/BoardDetail/Content/BoardDetailContent';
import ImageSwiper from '@/components/Swiper/ImageSwiper';
import BoardLikeButton from '@/components/@IntranetLibrary/Button/BoardLikeButton';
import {
  BackgroundBlockSx,
  BoardDetailSx,
  FileAttachmentWrapper,
} from '@/components/Board/BoardDetail/styles';
import AttachmentFile from '@/components/AttachmentFile';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import { LayoutType } from '@/constant/layout';
import useLayoutSize from '@/hooks/useLayoutSize';
import Head from 'next/head';
import { ActionType } from '@/constant/button';
import { BoardDetailHeaderId } from '@/components/Board/BoardDetailModal';
import classNames from 'classnames';
import { getFormattedNow } from '@/utils/format';
import { useAtom } from 'jotai';
import { socialState } from '@/recoil/atom';
import CreateBoardModal from '@/components/Board/CreateBoardModal';
import useModal from '@/hooks/useModal';
import { ModalRef } from '@/components/@IntranetLibrary/Modal';
import { useTranslation } from 'react-i18next';
import { boardWebPushCall } from '@/hooks/useNotificationTopics';
import BoardDetailFeedback from '@/components/Board/BoardDetail/Feedback';
import { BoardDetailLikesRef } from '@/components/Board/BoardDetail/Feedback/BoardDetailLikes';
import BoardDetailComment, {
  BoardDetailCommentID,
  BoardDetailCommentRef,
} from '@/components/Board/BoardDetail/Feedback/BoardDetailComment';
import useGlobalMutation from '@/hooks/useGlobalMutation';
import DeployedCodeViewer from '@/components/Board/BoardDetail/Content/DeployedCodeViewer';
import SandboxBottomBar from '@/components/Board/BoardDetail/Content/SandboxBottomBar';
import AttachmentDialog from '@/components/Board/BoardDetail/Content/AttachmentDialog';
import AttachmentSheet from '@/components/Board/BoardDetail/Content/AttachmentSheet';
import DesktopCommentSheet from '@/components/Board/BoardDetail/Content/DesktopCommentSheet';
import DesktopCommentLikeSection from '@/components/Board/BoardDetail/Content/DesktopCommentLikeSection';
import MobileCommentSheet from '@/components/Board/BoardDetail/Content/MobileCommentSheet';
import { useReactToPrint } from 'react-to-print';
import ExpandIcon from '@/public/icons/expand.svg';
import { GNBZIndex, NewNoticeZIndex } from '@/constant/z-index';

export type BoardDetailProps = {
  layoutType?: LayoutType;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  data: BoardDetailData;
  isRowDirection?: boolean;
  headerId?: string;
  disabled?: boolean;
  onDeleted?: (documentId: string) => void;
  onClose?: () => void;
  onEdited?: (id: string, changes: Record<string, any>) => void;
  onChange?: (id: string, changes: Record<string, any>) => void;
  mutate?: any;
  className?: string | undefined;
  sx?: SxProps<Theme>;
  previewDeployUrl?: string;
  isSandboxFullPage?: boolean;
  onSandboxFullPageChange?: (value: boolean) => void;
  showSandboxExpandControl?: boolean;
};

const BoardDetail = (props: BoardDetailProps) => {
  const {
    layoutType = LayoutType.DEFAULT,
    scrollRef,
    data,
    isRowDirection = false,
    headerId = BoardDetailHeaderId,
    disabled,
    onDeleted,
    onClose,
    onChange,
    mutate,
    className,
    sx,
    previewDeployUrl,
    isSandboxFullPage: controlledSandboxFullPage,
    onSandboxFullPageChange,
    showSandboxExpandControl = false,
  } = props;

  const { t } = useTranslation();
  const { isMobile } = useLayoutSize(layoutType);
  const sandboxFullPageIconSize = isMobile ? 28 : 24;
  const dialog = useDialog();
  const toast = useSnackbar();
  const isLoadingSet = new Set<ActionType>();
  const globalMutate = useGlobalMutation();

  const [state, setState] = useAtom(socialState);
  const [isBookmark, setIsBookmark] = useState(false);
  const [isLike, setIsLike] = useState(false);
  const [isLikePending, setIsLikePending] = useState(false);
  const [isRN, setIsRN] = useState(false);
  const isAppEmbedded = useIsAppEmbedded();
  const [internalSandboxFullPage, setInternalSandboxFullPage] = useState(false);
  const isSandboxFullPage =
    controlledSandboxFullPage ?? internalSandboxFullPage;
  const setSandboxFullPage = useCallback(
    (value: boolean) => {
      if (controlledSandboxFullPage === undefined) {
        setInternalSandboxFullPage(value);
      }
      onSandboxFullPageChange?.(value);
    },
    [controlledSandboxFullPage, onSandboxFullPageChange],
  );

  const boardDetailRef = useRef<HTMLElement | null>(null);
  const boardContentRef = useRef<HTMLElement | null>(null);
  const boardFeedbackWrapRef = useRef<HTMLElement | null>(null);
  const boardDetailLikesRef = useRef<BoardDetailLikesRef | null>(null);
  const boardDetailCommentRef = useRef<BoardDetailCommentRef | null>(null);

  const [headerHeight, setHeaderHeight] = useState(0);

  const [isEditing, setIsEditing] = useState(false);
  const [isAttachmentOpen, setIsAttachmentOpen] = useState(false);
  const [isDesktopCommentOpen, setIsDesktopCommentOpen] = useState(false);
  const createBoardModal = useRef<ModalRef>(null);
  const [isMobileAttachmentOpen, setIsMobileAttachmentOpen] = useState(false);
  const [isMobileCommentOpen, setIsMobileCommentOpen] = useState(false);
  const { openModal: openCreateBoardModal, closeModal: closeCreateBoardModal } =
    useModal(createBoardModal);
  const shouldHideMobileAttachmentSheet =
    data.config.isSandbox && isMobile && isRN;

  useEffect(() => {
    if (!data) return;
    setIsBookmark(data.feedback.isBookmark);
    setIsLike(data.feedback.isLike);
  }, [data]);

  useEffect(() => {
    setIsRN(!!(window.isRNWebview || window.isRNWebview2));
  }, []);

  useEffect(() => {
    const headerEl = document.getElementById(headerId);
    if (headerEl) setHeaderHeight(headerEl.clientHeight);
  }, [isRowDirection]);

  useEffect(() => {
    if (data.config.isSandbox && isMobile) return;
    setInternalSandboxFullPage(false);
  }, [data.config.isSandbox, isMobile]);

  useEffect(() => {
    if (
      controlledSandboxFullPage !== undefined ||
      !data.config.isSandbox ||
      !isMobile ||
      !internalSandboxFullPage
    ) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [
    controlledSandboxFullPage,
    data.config.isSandbox,
    internalSandboxFullPage,
    isMobile,
  ]);

  const scrollToCommentSection = useCallback(() => {
    if (data.config.isSandbox) {
      if (isMobile) {
        setIsMobileCommentOpen(true);
      } else {
        setIsDesktopCommentOpen(true);
      }
      return;
    }

    if (isRowDirection) {
      boardDetailLikesRef.current?.setIsOpen(false);
    } else {
      const element = document.getElementById(BoardDetailCommentID);
      if (!element) return;

      let offset = 76; // Header 크기
      if (boardDetailRef.current) {
        const style = getComputedStyle(boardDetailRef.current);
        offset += Number(
          style.getPropertyValue('--board-detail-offset-top').replace('px', ''),
        );
      }

      if (!scrollRef?.current) {
        scrollToElement(element, offset);
      } else {
        const scrollTarget =
          scrollRef?.current ||
          document.getElementsByClassName('modal-content')[0];
        if (scrollTarget) {
          scrollTarget.scrollTo({
            top: element.offsetTop - offset,
            behavior: 'smooth',
          });
        }
      }
    }
  }, [data.config.isSandbox, isMobile, isRowDirection, scrollRef]);

  const onAction = async (action: ActionType) => {
    if (isLoadingSet.has(action)) return;

    isLoadingSet.add(action);
    switch (action) {
      case ActionType.BOOKMARK: {
        const bookmark = !isBookmark;
        const result = await changeBoardBookmark(data.id, bookmark);

        if (result) {
          setIsBookmark(bookmark);
          onChange?.(data.id, { isBookmark: bookmark });
          mutate?.(
            { ...data, feedback: { ...data.feedback, isBookmark: bookmark } },
            false,
          );
          globalMutate('api/v1/board/filter/count', 'isBookmark=1');
        }
        break;
      }
      case ActionType.LIKE: {
        // Prevent repeated clicks while pending
        if (isLikePending) return;

        const like = !isLike;
        setIsLikePending(true);

        try {
          const result = await changeLikeBoard(data.id, like);

          if (result) {
            setIsLike(like);
            boardDetailLikesRef.current?.refresh();
            // Refresh feedback users cache for sandbox like summary and comment sheet
            globalMutate('feedback', data.id);
            const likeCount = (data.feedback.likesCount += like ? 1 : -1);
            onChange?.(data.id, { isLike: like, likeCount });
            mutate?.(
              { ...data, feedback: { ...data.feedback, isLike: like } },
              false,
            );
          }
        } finally {
          setIsLikePending(false);
        }
        break;
      }
      case ActionType.DELETE:
        dialog.show({
          message: '게시글을 삭제하시겠습니까?',
          cancelButtonText: '취소',
          confirmButtonText: '삭제',
          onConfirm: async () => {
            const result = await useDeleteBoard(data.id);
            if (result === true) {
              toast.show({
                severity: 'success',
                message: '게시글이 삭제되었습니다.',
              });
              if (onDeleted) onDeleted(data.id);
              if (!data.config.isReserved) {
                // 리프레쉬 푸쉬 발송은 예약 게시글이 아니어야 한다.
                boardWebPushCall();
              }

              setState({
                ...state,
                currentDate: getFormattedNow(),
              });
            } else {
              toast.showError({
                message: result || '삭제 실패. 새로고침 후 다시 시도해 주세요.',
              });
            }
          },
        });
        break;
      case ActionType.EDIT:
        setIsEditing(true);
        openCreateBoardModal();
        break;
    }

    isLoadingSet.delete(action);
  };

  const handleComment = useCallback(
    (isComment: boolean, commentCount: number) => {
      onChange?.(data.id, { isComment, commentCount });
    },
    [onChange, data.id],
  );

  const onEditSubmit = () => {
    toast.showSuccess({ message: t('게시글이 수정되었습니다.') });
    mutate?.();
  };

  const print = useReactToPrint({
    contentRef: boardContentRef,
  });

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no"
        />
      </Head>
      <Box
        ref={boardDetailRef}
        className={
          classNames({
            isRowDirection,
            isMobile,
            isSandbox: data.config.isSandbox,
            isSandboxFullPage: data.config.isSandbox && isSandboxFullPage,
          }) + ` ${className}`
        }
        sx={{
          // 게시글 수정 모달 open 시 안보이도록 opacity 처리
          opacity: isEditing ? 0 : 1,
          ...BoardDetailSx,
          ...sx,
          // Sandbox mode: flex column layout fills parent height
          ...(data.config.isSandbox && {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }),
          // Override paddingBottom for sandbox mode (modal: 96px, page: 152px)
          // &.isSandbox has higher specificity than root-level paddingBottom
          // and same specificity as &.isModal but comes later, so it wins
          '&.isSandbox': {
            paddingBottom: 0,
          },
          '&.isSandboxFullPage': {
            position: 'fixed',
            inset: 0,
            width: '100vw',
            height: '100dvh',
            minHeight: '100vh',
            zIndex: isMobile ? NewNoticeZIndex + 1 : GNBZIndex + 1,
            backgroundColor: 'var(--dk-bg, #FFFFFF)',
            borderRadius: 0,
            overflow: 'hidden',
            paddingLeft: 0,
            paddingRight: 0,
          },
          '&.isSandboxFullPage > header': {
            display: 'none',
          },
          '&.isSandboxFullPage .board-content-wrap': {
            marginLeft: 0,
            marginRight: 0,
            width: '100%',
          },
          '&.isSandboxFullPage .board-content': {
            paddingTop: '0px',
          },
          '&.isSandboxFullPage .sandbox-bottom-bar': {
            display: 'none',
          },
          '& .sandbox-page-float-restore': {
            position: 'absolute',
            top: isMobile ? 'max(12px, env(safe-area-inset-top))' : '12px',
            right: isMobile ? 'max(16px, env(safe-area-inset-right))' : '24px',
            zIndex: 200,
            width: isMobile ? '44px' : '40px',
            height: isMobile ? '44px' : '40px',
            padding: '8px',
            border: 'none',
            borderRadius: '999px',
            background: 'rgba(0, 0, 0, 0.24)',
            boxShadow: '0px 0px 16px rgba(0, 0, 0, 0.08)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.40)',
            },
          },
          '& .board-content': {
            paddingTop:
              data.config.isSandbox && isSandboxFullPage
                ? '0px'
                : `${headerHeight}px`,
          },
        }}
      >
        {data.config.isSandbox && isSandboxFullPage && (
          <button
            type="button"
            className="sandbox-page-float-restore"
            aria-label="축소"
            onClick={() => setSandboxFullPage(false)}
          >
            <svg
              width={sandboxFullPageIconSize}
              height={sandboxFullPageIconSize}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M19.082 12.7217C19.4132 12.7219 19.6816 12.991 19.6816 13.3223C19.6813 13.6531 19.4129 13.9216 19.082 13.9219L14.7705 13.9209L19.0254 18.1758C19.2596 18.4101 19.2606 18.7901 19.0264 19.0244C18.7921 19.2587 18.4121 19.2576 18.1777 19.0234L13.8018 14.6475L13.8027 19.2012C13.8025 19.5323 13.5333 19.8017 13.2021 19.8018C12.8711 19.8016 12.6028 19.5322 12.6025 19.2012V13.3223C12.6025 12.991 12.8709 12.7219 13.2021 12.7217H19.082ZM10.6816 4.19727C11.0129 4.19727 11.282 4.46667 11.2822 4.79785V10.6768C11.2822 11.0081 11.013 11.2764 10.6816 11.2764H4.80273C4.47141 11.2763 4.20313 11.0081 4.20312 10.6768C4.20345 10.346 4.47103 10.0777 4.80176 10.0771H9.23145L5.16602 6.01172C4.9317 5.77741 4.9317 5.39838 5.16602 5.16406C5.40033 4.92975 5.77936 4.92975 6.01367 5.16406L10.082 9.23242V4.79785C10.0823 4.46683 10.3506 4.19753 10.6816 4.19727Z"
                fill="white"
              />
            </svg>
          </button>
        )}

        {/* iOS 에서 스크롤 시 Header 위, 옆 틈으로 게시글 내용이 보여서 가리기 위함 */}
        {/* 운영 배포를 위한 처리. 추후 게시글 코드 리팩토링 시 깨끗한 코드로 변경 필요 */}
        {isMobile && !(data.config.isSandbox && isSandboxFullPage) && (
          <Box sx={BackgroundBlockSx} />
        )}

        {!(data.config.isSandbox && isSandboxFullPage && isMobile) && (
          <BoardDetailHeader
            isMobile={isMobile}
            id={headerId}
            scrollRef={scrollRef}
            disabled={disabled}
            onClickComment={scrollToCommentSection}
            onAction={onAction}
            onPrint={print}
            data={data}
            isBookmark={isBookmark}
            // 샌드박스 모바일은 원래 X 가 있었고, 네이티브 앱이 헤더 없이 띄운 화면(embedded)은 일반 글도 X 로 닫는다
            onClose={
              isMobile && (data.config.isSandbox || isAppEmbedded)
                ? onClose
                : undefined
            }
            extraActionNode={
              data.config.isSandbox &&
              showSandboxExpandControl &&
              !isSandboxFullPage ? (
                <Button
                  disabled={disabled}
                  aria-label="전체화면"
                  onClick={() => setSandboxFullPage(true)}
                  sx={{
                    padding: 0,
                    width: sandboxFullPageIconSize,
                    height: sandboxFullPageIconSize,
                    minWidth: sandboxFullPageIconSize,
                  }}
                >
                  {isMobile ? (
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 28 28"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        d="M15.1455 15.1455C15.3798 14.9113 15.7598 14.9114 15.9941 15.1455L21.7305 20.8818V15.6113C21.7307 15.2802 21.9999 15.0117 22.3311 15.0117C22.662 15.012 22.9304 15.2803 22.9307 15.6113V22.4707C22.9307 22.8021 22.6614 23.0713 22.3301 23.0713H15.4717C15.1403 23.0713 14.8721 22.8021 14.8721 22.4707C14.8723 22.1397 15.1397 21.8714 15.4707 21.8711H21.0234L15.1455 15.9932C14.9118 15.7588 14.9115 15.3796 15.1455 15.1455ZM12.3887 4.92969C12.72 4.92971 12.9883 5.19892 12.9883 5.53027C12.988 5.86121 12.7206 6.1286 12.3896 6.12891H6.97656L12.374 11.5264C12.608 11.7607 12.6082 12.1398 12.374 12.374C12.1398 12.6081 11.7607 12.6079 11.5264 12.374L6.12988 6.97754V12.3887C6.12987 12.72 5.86066 12.9893 5.5293 12.9893C5.19816 12.989 4.9297 12.7199 4.92969 12.3887V5.53027C4.92969 5.1989 5.1989 4.92969 5.53027 4.92969H12.3887Z"
                        fill="black"
                      />
                    </svg>
                  ) : (
                    <ExpandIcon
                      width={sandboxFullPageIconSize}
                      height={sandboxFullPageIconSize}
                    />
                  )}
                </Button>
              ) : undefined
            }
          />
        )}

        <Stack
          direction={'column'}
          alignItems={'stretch'}
          className={'board-content-wrap'}
          sx={
            data.config.isSandbox
              ? {
                  flex: '1 1 0',
                  minHeight: 0,
                  width: '100%',
                  minWidth: 0,
                  position: 'relative', // Required for DesktopCommentSheet absolute positioning
                }
              : undefined
          }
        >
          <Box
            ref={boardContentRef}
            component={'section'}
            className={'board-content'}
            sx={
              data.config.isSandbox
                ? {
                    flex: '1 1 0',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    overflow: 'hidden',
                    width: '100%',
                    minWidth: 0,
                    alignSelf: 'stretch',
                  }
                : undefined
            }
          >
            {data.config.isSandbox ? (
              <DeployedCodeViewer
                boardId={data.id}
                previewUrl={previewDeployUrl}
                updatedAt={data.updatedAt}
                enableFullScreen={false}
                fillContainer
              />
            ) : (
              <>
                {data.content && <BoardDetailContent content={data.content} />}
                {data.config.viewType === BoardViewType.SLIDE && (
                  <ImageSwiper
                    imageList={data.files.slideImages.map((item) => item.link)}
                    component={'section'}
                    sx={{ margin: '56px auto 44px' }}
                  />
                )}
                {data.files.attachments.length > 0 && (
                  <FileAttachmentWrapper sx={{ marginTop: '60px' }}>
                    {data.files.attachments.map((file, index) => (
                      <AttachmentFile
                        key={index}
                        file={file}
                        disabled={disabled}
                        isDeletable={false}
                        isPreviewable={true}
                      />
                    ))}
                  </FileAttachmentWrapper>
                )}
                <BoardLikeButton
                  isLike={isLike}
                  disabled={disabled}
                  onClick={() => onAction(ActionType.LIKE)}
                  sx={{ display: 'flex', margin: '32px auto' }}
                />
              </>
            )}
          </Box>
          {data.config.isSandbox ? (
            <Box className="sandbox-bottom-bar">
              <SandboxBottomBar
                documentId={data.id}
                commentGroupId={data.commentGroupId ?? ''}
                isLike={isLike}
                isLikePending={isLikePending}
                attachmentCount={data.files.attachments.length}
                disabled={disabled}
                isMobile={isMobile}
                showAttachmentButton={!shouldHideMobileAttachmentSheet}
                onLikeClick={() => onAction(ActionType.LIKE)}
                onCommentClick={() => {
                  if (isMobile) {
                    setIsMobileCommentOpen(true);
                  } else {
                    setIsDesktopCommentOpen(true);
                  }
                }}
                onAttachmentClick={() => {
                  if (isMobile) {
                    setIsMobileAttachmentOpen(true);
                  } else {
                    setIsAttachmentOpen(true);
                  }
                }}
              />
            </Box>
          ) : (
            <Box
              ref={boardFeedbackWrapRef}
              component={'section'}
              className={'board-feedback-wrap'}
            >
              <BoardDetailFeedback
                id={'board-feedback'}
                data={data}
                disabled={disabled}
                boardDetailLikesRef={boardDetailLikesRef}
                boardDetailCommentRef={boardDetailCommentRef}
                isRowDirection={isRowDirection}
                isMobile={isMobile}
                handleComment={handleComment}
              />
            </Box>
          )}
          {/* Desktop: In-container comment sheet overlay */}
          {data.config.isSandbox && !isMobile && (
            <DesktopCommentSheet
              open={isDesktopCommentOpen}
              onClose={() => setIsDesktopCommentOpen(false)}
            >
              {/* Like section at top with expand/collapse and separate like button */}
              <DesktopCommentLikeSection
                documentId={data.id}
                isLike={isLike}
                isLikePending={isLikePending}
                disabled={disabled}
                onLikeClick={() => onAction(ActionType.LIKE)}
                sx={{ marginBottom: '16px' }}
              />
              {/* Divider - extends through sheet padding with negative margins */}
              <Box
                sx={{
                  height: '3px',
                  backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
                  marginBottom: '16px',
                  marginLeft: '-24px',
                  marginRight: '-24px',
                }}
              />
              {/* Comment section with sheet layout: input → header → list */}
              <BoardDetailComment
                commentGroupId={data.commentGroupId}
                disabled={disabled}
                inputStyle="sheet"
                service="board"
                handleComment={handleComment}
              />
            </DesktopCommentSheet>
          )}
        </Stack>
      </Box>
      {data.config.isDeleteEnable && (
        <CreateBoardModal
          ref={createBoardModal}
          documentId={data.id}
          handleClose={() => {
            setIsEditing(false);
            closeCreateBoardModal();
          }}
          onSubmit={onEditSubmit}
        />
      )}
      {/* Mobile: Mount AttachmentSheet when sandbox + mobile + has attachments */}
      {data.config.isSandbox &&
        isMobile &&
        !shouldHideMobileAttachmentSheet &&
        data.files.attachments.length > 0 && (
          <AttachmentSheet
            open={isMobileAttachmentOpen}
            onClose={() => setIsMobileAttachmentOpen(false)}
            files={data.files.attachments}
            disabled={disabled}
          />
        )}
      {/* Mobile: Mount MobileCommentSheet when sandbox + mobile */}
      {data.config.isSandbox && isMobile && (
        <MobileCommentSheet
          open={isMobileCommentOpen}
          onClose={() => setIsMobileCommentOpen(false)}
          documentId={data.id}
          commentGroupId={data.commentGroupId}
          isLike={isLike}
          isLikePending={isLikePending}
          disabled={disabled}
          onLikeClick={() => onAction(ActionType.LIKE)}
          handleComment={handleComment}
        />
      )}
      {/* Desktop: Mount AttachmentDialog only when open */}
      {data.config.isSandbox && !isMobile && isAttachmentOpen && (
        <AttachmentDialog
          open={isAttachmentOpen}
          onClose={() => setIsAttachmentOpen(false)}
          files={data.files.attachments}
          disabled={disabled}
        />
      )}
    </>
  );
};

export default BoardDetail;
